/**
 * Management client — the authed half of `@peacock/sdk-js`.
 *
 * Where `createClient()` is anonymous and hits `/v1/cdn/...`, the
 * management client carries a bearer token and hits the Sanctum-
 * protected `/v1/spaces/{slug}/...` endpoints. Used by the React
 * admin (Phase 3) and the MCP server (Phase 6).
 *
 * Same design choices as the CDN client:
 *  - Pure ESM, no runtime deps
 *  - Optional `fetch` injection for tests
 *  - Discriminated-union results instead of exceptions
 *
 * Why a separate factory and not a `withToken()` method on
 * `PeacockClient`: the CDN client is meant to be safe to ship in
 * browser bundles; bolting a token-carrying method onto it would
 * tempt users to embed an admin token in client-side code. Keeping
 * the two factories distinct makes the trust boundary visible.
 */
// Standalone onboarding helper — unauth'd endpoint, no token in header.
export async function onboard(baseUrl, payload, fetchImpl = globalThis.fetch) {
    const response = await fetchImpl(`${baseUrl.replace(/\/+$/, '')}/v1/onboard`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
    });
    if (response.status === 422) {
        return { status: 'error', code: 422, response };
    }
    if (!response.ok) {
        return { status: 'error', code: response.status, response };
    }
    const data = (await response.json());
    return { status: 'ok', data };
}
async function postUnauth(baseUrl, path, payload, fetchImpl) {
    const response = await fetchImpl(`${baseUrl.replace(/\/+$/, '')}${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
    });
    if (!response.ok) {
        return { status: 'error', code: response.status, response };
    }
    // 204 no-body responses (logout) shouldn't try to JSON-parse.
    if (response.status === 204) {
        return { status: 'ok', data: undefined };
    }
    const data = (await response.json());
    return { status: 'ok', data };
}
export function authRegister(baseUrl, payload, fetchImpl = globalThis.fetch) {
    return postUnauth(baseUrl, '/v1/auth/register', payload, fetchImpl);
}
export function authLogin(baseUrl, payload, fetchImpl = globalThis.fetch) {
    return postUnauth(baseUrl, '/v1/auth/login', payload, fetchImpl);
}
export function createManagementClient(options) {
    if (typeof options.baseUrl !== 'string' || options.baseUrl.length === 0) {
        throw new Error('createManagementClient: `baseUrl` is required');
    }
    if (options.baseUrl.endsWith('/')) {
        throw new Error('createManagementClient: `baseUrl` must NOT include a trailing slash');
    }
    if (typeof options.token !== 'string' || options.token.length === 0) {
        throw new Error('createManagementClient: `token` is required');
    }
    const fetchImpl = options.fetch ?? globalThis.fetch;
    if (typeof fetchImpl !== 'function') {
        throw new Error('createManagementClient: no fetch implementation available');
    }
    // The token is deliberately NOT exposed via `config` — read access
    // would let a future bug or DevTools inspection leak it.
    const publicConfig = {
        baseUrl: options.baseUrl,
    };
    async function request(path, init = {}) {
        const headers = new Headers();
        headers.set('Accept', 'application/json');
        headers.set('Authorization', `Bearer ${options.token}`);
        if (init.body !== undefined) {
            headers.set('Content-Type', 'application/json');
        }
        const response = await fetchImpl(`${options.baseUrl}${path}`, {
            method: init.method ?? 'GET',
            headers,
            body: init.body === undefined ? undefined : JSON.stringify(init.body),
        });
        if (response.status === 401) {
            return { status: 'unauthenticated' };
        }
        if (response.status === 403) {
            let code = 'forbidden';
            try {
                const body = (await response.json());
                if (typeof body.error === 'string') {
                    code = body.error;
                }
            }
            catch {
                // Body wasn't JSON; default code is fine.
            }
            return { status: 'forbidden', code };
        }
        if (response.status === 404) {
            return { status: 'not-found' };
        }
        if (!response.ok) {
            return { status: 'error', code: response.status, response };
        }
        const data = (await response.json());
        return { status: 'ok', data };
    }
    /**
     * DELETE-style request that doesn't parse a body. Returns a
     * `ManagementResult<void>` and short-circuits on 204. Used by the
     * Phase 10 archive/delete endpoints so the caller doesn't get a
     * spurious "expected JSON" error when the server returns 204.
     */
    async function requestNoBody(path) {
        const headers = new Headers();
        headers.set('Accept', 'application/json');
        headers.set('Authorization', `Bearer ${options.token}`);
        const response = await fetchImpl(`${options.baseUrl}${path}`, {
            method: 'DELETE',
            headers,
        });
        if (response.status === 401)
            return { status: 'unauthenticated' };
        if (response.status === 403) {
            let code = 'forbidden';
            try {
                const body = (await response.json());
                if (typeof body.error === 'string')
                    code = body.error;
            }
            catch {
                // default code is fine
            }
            return { status: 'forbidden', code };
        }
        if (response.status === 404)
            return { status: 'not-found' };
        if (!response.ok) {
            return { status: 'error', code: response.status, response };
        }
        return { status: 'ok', data: undefined };
    }
    return {
        config: Object.freeze(publicConfig),
        async whoami() {
            const result = await request('/v1/whoami');
            if (result.status === 'ok') {
                return { status: 'ok', data: result.data.data };
            }
            return result;
        },
        async me() {
            const result = await request('/v1/auth/me');
            if (result.status === 'ok') {
                return { status: 'ok', data: result.data.data };
            }
            return result;
        },
        async logout() {
            const result = await request('/v1/auth/logout', { method: 'POST' });
            return result;
        },
        async createSpace(payload) {
            const result = await request('/v1/spaces', { method: 'POST', body: payload });
            if (result.status === 'ok') {
                return { status: 'ok', data: result.data.data };
            }
            return result;
        },
        async createStory(spaceSlug, payload) {
            const result = await request(`/v1/spaces/${encodeURIComponent(spaceSlug)}/stories`, { method: 'POST', body: payload });
            if (result.status === 'ok') {
                return { status: 'ok', data: result.data.data };
            }
            return result;
        },
        async listStories(spaceSlug, opts = {}) {
            const params = new URLSearchParams();
            if (opts.parentId !== undefined) {
                params.set('parent_id', String(opts.parentId));
            }
            if (opts.startsWith !== undefined) {
                params.set('starts_with', opts.startsWith);
            }
            if (opts.lang !== undefined) {
                params.set('lang', opts.lang);
            }
            if (opts.perPage !== undefined) {
                params.set('per_page', String(opts.perPage));
            }
            const qs = params.toString();
            const suffix = qs.length > 0 ? `?${qs}` : '';
            return request(`/v1/spaces/${encodeURIComponent(spaceSlug)}/stories${suffix}`);
        },
        async getStory(spaceSlug, storyUuid) {
            return request(`/v1/spaces/${encodeURIComponent(spaceSlug)}/stories/${encodeURIComponent(storyUuid)}`);
        },
        async saveVersion(spaceSlug, storyUuid, payload) {
            return request(`/v1/spaces/${encodeURIComponent(spaceSlug)}/stories/${encodeURIComponent(storyUuid)}`, { method: 'PUT', body: payload });
        },
        async listAssets(spaceSlug, opts = {}) {
            const params = new URLSearchParams();
            if (opts.perPage !== undefined) {
                params.set('per_page', String(opts.perPage));
            }
            if (opts.page !== undefined) {
                params.set('page', String(opts.page));
            }
            const qs = params.toString();
            const suffix = qs.length > 0 ? `?${qs}` : '';
            return request(`/v1/spaces/${encodeURIComponent(spaceSlug)}/assets${suffix}`);
        },
        async patchAsset(spaceSlug, assetUuid, payload) {
            return request(`/v1/spaces/${encodeURIComponent(spaceSlug)}/assets/${encodeURIComponent(assetUuid)}`, { method: 'PATCH', body: payload });
        },
        async uploadAsset(spaceSlug, file, fileName) {
            // Multipart upload — distinct from the JSON `request()` helper
            // because we need a `FormData` body and want fetch to set the
            // multipart boundary on Content-Type automatically.
            const form = new FormData();
            form.append('file', file, fileName);
            const headers = new Headers();
            headers.set('Accept', 'application/json');
            headers.set('Authorization', `Bearer ${options.token}`);
            const response = await fetchImpl(`${options.baseUrl}/v1/spaces/${encodeURIComponent(spaceSlug)}/assets`, { method: 'POST', headers, body: form });
            if (response.status === 401)
                return { status: 'unauthenticated' };
            if (response.status === 403) {
                let code = 'forbidden';
                try {
                    const body = (await response.json());
                    if (typeof body.error === 'string')
                        code = body.error;
                }
                catch {
                    // fall through with default
                }
                return { status: 'forbidden', code };
            }
            if (response.status === 404)
                return { status: 'not-found' };
            if (!response.ok) {
                return { status: 'error', code: response.status, response };
            }
            const data = (await response.json());
            return { status: 'ok', data };
        },
        async listLocales(spaceSlug) {
            return request(`/v1/spaces/${encodeURIComponent(spaceSlug)}/locales`);
        },
        // ─── Phase 1 leftover ───
        async listBlueprints(spaceSlug) {
            return request(`/v1/spaces/${encodeURIComponent(spaceSlug)}/component-blueprints`);
        },
        // ─── Phase 4 ───
        async listVersions(spaceSlug, storyUuid) {
            return request(`/v1/spaces/${encodeURIComponent(spaceSlug)}/stories/${encodeURIComponent(storyUuid)}/versions`);
        },
        async diffVersions(spaceSlug, storyUuid, from, to) {
            const qs = new URLSearchParams({ from: String(from), to: String(to) }).toString();
            return request(`/v1/spaces/${encodeURIComponent(spaceSlug)}/stories/${encodeURIComponent(storyUuid)}/diff?${qs}`);
        },
        async restoreVersion(spaceSlug, storyUuid, versionNo) {
            return request(`/v1/spaces/${encodeURIComponent(spaceSlug)}/stories/${encodeURIComponent(storyUuid)}/restore`, { method: 'POST', body: { version_no: versionNo } });
        },
        async transitionStory(spaceSlug, storyUuid, toStage, message) {
            return request(`/v1/spaces/${encodeURIComponent(spaceSlug)}/stories/${encodeURIComponent(storyUuid)}/transition`, { method: 'POST', body: message === undefined ? { to_stage: toStage } : { to_stage: toStage, message } });
        },
        // ─── Phase 14 — approvals ───
        async listApprovals(spaceSlug, storyUuid) {
            return request(`/v1/spaces/${encodeURIComponent(spaceSlug)}/stories/${encodeURIComponent(storyUuid)}/approvals`);
        },
        async voteOnTransition(spaceSlug, storyUuid, stage, vote, message) {
            const body = { stage, vote };
            if (message !== undefined)
                body['message'] = message;
            return request(`/v1/spaces/${encodeURIComponent(spaceSlug)}/stories/${encodeURIComponent(storyUuid)}/approvals`, { method: 'POST', body });
        },
        async withdrawApproval(spaceSlug, storyUuid, stage) {
            return request(`/v1/spaces/${encodeURIComponent(spaceSlug)}/stories/${encodeURIComponent(storyUuid)}/approvals`, { method: 'DELETE', body: { stage } });
        },
        // ─── Phase 15 — Collaboration ───
        async listPresence(spaceSlug, storyUuid) {
            return request(`/v1/spaces/${encodeURIComponent(spaceSlug)}/stories/${encodeURIComponent(storyUuid)}/presence`);
        },
        async pingPresence(spaceSlug, storyUuid, label) {
            return request(`/v1/spaces/${encodeURIComponent(spaceSlug)}/stories/${encodeURIComponent(storyUuid)}/presence/ping`, { method: 'POST', body: label === undefined ? {} : { label } });
        },
        async listComments(spaceSlug, storyUuid, includeResolved) {
            const qs = includeResolved ? '?includeResolved=1' : '';
            return request(`/v1/spaces/${encodeURIComponent(spaceSlug)}/stories/${encodeURIComponent(storyUuid)}/comments${qs}`);
        },
        async postComment(spaceSlug, storyUuid, body, opts = {}) {
            const payload = { body };
            if (opts.blockPath !== undefined)
                payload['block_path'] = opts.blockPath;
            if (opts.parentId !== undefined)
                payload['parent_id'] = opts.parentId;
            return request(`/v1/spaces/${encodeURIComponent(spaceSlug)}/stories/${encodeURIComponent(storyUuid)}/comments`, { method: 'POST', body: payload });
        },
        async resolveComment(spaceSlug, storyUuid, commentId) {
            return request(`/v1/spaces/${encodeURIComponent(spaceSlug)}/stories/${encodeURIComponent(storyUuid)}/comments/${commentId}/resolve`, { method: 'POST' });
        },
        async deleteComment(spaceSlug, storyUuid, commentId) {
            return request(`/v1/spaces/${encodeURIComponent(spaceSlug)}/stories/${encodeURIComponent(storyUuid)}/comments/${commentId}`, { method: 'DELETE' });
        },
        // ─── Phase 16 — Plugins ───
        async listPlugins(spaceSlug, status) {
            const qs = status ? `?status=${encodeURIComponent(status)}` : '';
            return request(`/v1/spaces/${encodeURIComponent(spaceSlug)}/plugins${qs}`);
        },
        async createPlugin(spaceSlug, plugin) {
            return request(`/v1/spaces/${encodeURIComponent(spaceSlug)}/plugins`, { method: 'POST', body: plugin });
        },
        async getPlugin(spaceSlug, pluginSlug) {
            return request(`/v1/spaces/${encodeURIComponent(spaceSlug)}/plugins/${encodeURIComponent(pluginSlug)}`);
        },
        async updatePlugin(spaceSlug, pluginSlug, patch) {
            return request(`/v1/spaces/${encodeURIComponent(spaceSlug)}/plugins/${encodeURIComponent(pluginSlug)}`, { method: 'PUT', body: patch });
        },
        async activatePlugin(spaceSlug, pluginSlug) {
            return request(`/v1/spaces/${encodeURIComponent(spaceSlug)}/plugins/${encodeURIComponent(pluginSlug)}/activate`, { method: 'POST' });
        },
        async deactivatePlugin(spaceSlug, pluginSlug) {
            return request(`/v1/spaces/${encodeURIComponent(spaceSlug)}/plugins/${encodeURIComponent(pluginSlug)}/deactivate`, { method: 'POST' });
        },
        async deletePlugin(spaceSlug, pluginSlug) {
            return request(`/v1/spaces/${encodeURIComponent(spaceSlug)}/plugins/${encodeURIComponent(pluginSlug)}`, { method: 'DELETE' });
        },
        async pluginEvents(spaceSlug, pluginSlug) {
            return request(`/v1/spaces/${encodeURIComponent(spaceSlug)}/plugins/${encodeURIComponent(pluginSlug)}/events`);
        },
        async pluginManifest(spaceSlug) {
            return request(`/v1/spaces/${encodeURIComponent(spaceSlug)}/plugins/manifest`);
        },
        async rotatePluginSecret(spaceSlug, pluginSlug) {
            return request(`/v1/spaces/${encodeURIComponent(spaceSlug)}/plugins/${encodeURIComponent(pluginSlug)}/rotate-secret`, { method: 'POST' });
        },
        // ─── Phase 5 — AI ───
        async aiStatus(spaceSlug) {
            return request(`/v1/spaces/${encodeURIComponent(spaceSlug)}/ai/status`);
        },
        async aiText(spaceSlug, prompt, opts = {}) {
            const body = { prompt };
            if (opts.context !== undefined)
                body['context'] = opts.context;
            if (opts.maxWords !== undefined)
                body['max_words'] = opts.maxWords;
            return request(`/v1/spaces/${encodeURIComponent(spaceSlug)}/ai/text`, { method: 'POST', body });
        },
        async aiTranslate(spaceSlug, source, fromLocale, toLocale, context) {
            const body = { source, from_locale: fromLocale, to_locale: toLocale };
            if (context !== undefined)
                body['context'] = context;
            return request(`/v1/spaces/${encodeURIComponent(spaceSlug)}/ai/translate`, { method: 'POST', body });
        },
        async aiAltText(spaceSlug, assetUuid, locale, contextHeadline) {
            const body = { asset_uuid: assetUuid, locale };
            if (contextHeadline !== undefined)
                body['context_headline'] = contextHeadline;
            return request(`/v1/spaces/${encodeURIComponent(spaceSlug)}/ai/alt-text`, { method: 'POST', body });
        },
        async aiSeoMeta(spaceSlug, storyUuid) {
            return request(`/v1/spaces/${encodeURIComponent(spaceSlug)}/ai/seo-meta`, { method: 'POST', body: { story_uuid: storyUuid } });
        },
        async aiInternalLinks(spaceSlug, storyUuid) {
            return request(`/v1/spaces/${encodeURIComponent(spaceSlug)}/ai/internal-links`, { method: 'POST', body: { story_uuid: storyUuid } });
        },
        async aiStoryInsights(spaceSlug, storyUuid) {
            return request(`/v1/spaces/${encodeURIComponent(spaceSlug)}/ai/story-insights`, { method: 'POST', body: { story_uuid: storyUuid } });
        },
        // ─── Phase 6 — AiJob audit + approval ───
        async listAiJobs(spaceSlug, opts = {}) {
            const params = new URLSearchParams();
            if (opts.approvalStatus !== undefined)
                params.set('approval_status', opts.approvalStatus);
            if (opts.action !== undefined)
                params.set('action', opts.action);
            if (opts.perPage !== undefined)
                params.set('per_page', String(opts.perPage));
            const qs = params.toString();
            return request(`/v1/spaces/${encodeURIComponent(spaceSlug)}/ai-jobs${qs ? `?${qs}` : ''}`);
        },
        async approveAiJob(spaceSlug, jobId) {
            return request(`/v1/spaces/${encodeURIComponent(spaceSlug)}/ai-jobs/${jobId}/approve`, { method: 'POST' });
        },
        async rejectAiJob(spaceSlug, jobId) {
            return request(`/v1/spaces/${encodeURIComponent(spaceSlug)}/ai-jobs/${jobId}/reject`, { method: 'POST' });
        },
        // ─── Phase 10 — Audience Segments + Story Variants ───
        async listSegments(spaceSlug, opts = {}) {
            const params = new URLSearchParams();
            if (opts.includeArchived === true)
                params.set('include_archived', '1');
            const qs = params.toString();
            return request(`/v1/spaces/${encodeURIComponent(spaceSlug)}/audience-segments${qs ? `?${qs}` : ''}`);
        },
        async getSegment(spaceSlug, key) {
            return request(`/v1/spaces/${encodeURIComponent(spaceSlug)}/audience-segments/${encodeURIComponent(key)}`);
        },
        async createSegment(spaceSlug, payload) {
            return request(`/v1/spaces/${encodeURIComponent(spaceSlug)}/audience-segments`, { method: 'POST', body: payload });
        },
        async updateSegment(spaceSlug, key, payload) {
            return request(`/v1/spaces/${encodeURIComponent(spaceSlug)}/audience-segments/${encodeURIComponent(key)}`, { method: 'PUT', body: payload });
        },
        async archiveSegment(spaceSlug, key) {
            // DELETE on this route is a soft-archive on the server side —
            // the segment row stays around so its variants survive.
            return requestNoBody(`/v1/spaces/${encodeURIComponent(spaceSlug)}/audience-segments/${encodeURIComponent(key)}`);
        },
        async listVariants(spaceSlug, storyUuid) {
            return request(`/v1/spaces/${encodeURIComponent(spaceSlug)}/stories/${encodeURIComponent(storyUuid)}/variants`);
        },
        async upsertVariant(spaceSlug, storyUuid, segmentKey, payload) {
            return request(`/v1/spaces/${encodeURIComponent(spaceSlug)}/stories/${encodeURIComponent(storyUuid)}/variants/${encodeURIComponent(segmentKey)}`, { method: 'PUT', body: payload });
        },
        async deleteVariant(spaceSlug, storyUuid, segmentKey) {
            // Server returns 204; reuse the existing `request()` (it handles
            // 204 by leaving `data` as the response object — but since the
            // ManagementResult shape doesn't carry it for `ok` of `void`,
            // and the request helper returns `{ status: 'ok', data: data }`
            // where data may be undefined for empty bodies — the caller
            // doesn't need the body, just the status.
            return requestNoBody(`/v1/spaces/${encodeURIComponent(spaceSlug)}/stories/${encodeURIComponent(storyUuid)}/variants/${encodeURIComponent(segmentKey)}`);
        },
        // ─── Phase 11: Datasources ───────────────────────────────────────
        async listDatasources(spaceSlug, opts = {}) {
            const qs = opts.includeArchived ? 'include_archived=1' : '';
            return request(`/v1/spaces/${encodeURIComponent(spaceSlug)}/datasources${qs ? `?${qs}` : ''}`);
        },
        async getDatasource(spaceSlug, key) {
            return request(`/v1/spaces/${encodeURIComponent(spaceSlug)}/datasources/${encodeURIComponent(key)}`);
        },
        async createDatasource(spaceSlug, payload) {
            return request(`/v1/spaces/${encodeURIComponent(spaceSlug)}/datasources`, { method: 'POST', body: payload });
        },
        async updateDatasource(spaceSlug, key, payload) {
            return request(`/v1/spaces/${encodeURIComponent(spaceSlug)}/datasources/${encodeURIComponent(key)}`, { method: 'PUT', body: payload });
        },
        async archiveDatasource(spaceSlug, key) {
            return requestNoBody(`/v1/spaces/${encodeURIComponent(spaceSlug)}/datasources/${encodeURIComponent(key)}`);
        },
        async syncDatasource(spaceSlug, key) {
            // Server returns 202 — the job is queued, not executed inline.
            // Caller polls listDatasourceEntries() afterward to see new rows.
            return request(`/v1/spaces/${encodeURIComponent(spaceSlug)}/datasources/${encodeURIComponent(key)}/sync`, { method: 'POST' });
        },
        async listDatasourceEntries(spaceSlug, key, opts = {}) {
            const params = [];
            if (opts.q)
                params.push(`q=${encodeURIComponent(opts.q)}`);
            if (opts.limit !== undefined)
                params.push(`limit=${opts.limit}`);
            const qs = params.length ? `?${params.join('&')}` : '';
            return request(`/v1/spaces/${encodeURIComponent(spaceSlug)}/datasources/${encodeURIComponent(key)}/entries${qs}`);
        },
        // ─── Phase 12: Analytics ─────────────────────────────────────────
        async getStoryAnalytics(spaceSlug, storyUuid, opts = {}) {
            const qs = opts.range ? `?range=${encodeURIComponent(opts.range)}` : '';
            return request(`/v1/spaces/${encodeURIComponent(spaceSlug)}/stories/${encodeURIComponent(storyUuid)}/analytics${qs}`);
        },
        async getAnalyticsOverview(spaceSlug, opts = {}) {
            const qs = opts.range ? `?range=${encodeURIComponent(opts.range)}` : '';
            return request(`/v1/spaces/${encodeURIComponent(spaceSlug)}/analytics/overview${qs}`);
        },
        // ─── Phase 13: Bulk + Export + Import ────────────────────────────
        async bulkStoryAction(spaceSlug, action, storyUuids) {
            return request(`/v1/spaces/${encodeURIComponent(spaceSlug)}/stories/bulk`, { method: 'POST', body: { action, story_uuids: storyUuids } });
        },
        exportStoriesUrl(spaceSlug, format) {
            // Returns the URL; caller appends Authorization header and fetches.
            // Building this client-side keeps the SDK from buffering large
            // exports into memory and lets the browser handle the download
            // via window.location or a hidden anchor.
            return `${options.baseUrl}/v1/spaces/${encodeURIComponent(spaceSlug)}/stories/export?format=${encodeURIComponent(format)}`;
        },
        async importStories(spaceSlug, file) {
            const fd = new FormData();
            fd.append('file', file);
            // For multipart, go direct so the browser sets the right
            // Content-Type with boundary. request() forces JSON content.
            const url = `${options.baseUrl}/v1/spaces/${encodeURIComponent(spaceSlug)}/stories/import`;
            const response = await fetchImpl(url, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${options.token}`,
                    Accept: 'application/json',
                },
                body: fd,
            });
            if (response.status === 422) {
                return { status: 'error', code: 422, response };
            }
            if (!response.ok) {
                return { status: 'error', code: response.status, response };
            }
            const data = (await response.json());
            return { status: 'ok', data };
        },
        // ─── Phase 7 (real): Billing ─────────────────────────────────────
        async getBilling(spaceSlug) {
            return request(`/v1/spaces/${encodeURIComponent(spaceSlug)}/billing`);
        },
        async createCheckoutSession(spaceSlug, plan) {
            // Server returns `{data: {checkout_url}}`; unwrap to keep the
            // consumer ergonomics identical to other SDK POSTs.
            const raw = await request(`/v1/spaces/${encodeURIComponent(spaceSlug)}/billing/checkout`, { method: 'POST', body: { plan } });
            if (raw.status !== 'ok')
                return raw;
            return { status: 'ok', data: raw.data.data };
        },
        async createPortalSession(spaceSlug) {
            const raw = await request(`/v1/spaces/${encodeURIComponent(spaceSlug)}/billing/portal`, { method: 'POST' });
            if (raw.status !== 'ok')
                return raw;
            return { status: 'ok', data: raw.data.data };
        },
        async publish(spaceSlug, storyUuid, versionNo) {
            // When `versionNo` is omitted we send NO body — that's the
            // "publish latest" signal. Previously we sent `{}` which set
            // `Content-Type: application/json` on what's effectively a
            // body-less POST; some intermediate proxies log that as a
            // suspicious-looking semi-payload. Empty body is cleaner.
            return request(`/v1/spaces/${encodeURIComponent(spaceSlug)}/stories/${encodeURIComponent(storyUuid)}/publish`, versionNo === undefined
                ? { method: 'POST' }
                : { method: 'POST', body: { version_no: versionNo } });
        },
    };
}
//# sourceMappingURL=management.js.map